import { useEffect, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { M3 } from '@messenger/consts'
import {
  applicationTypeAtom,
  selectedProductsAtom,
  productsAtom,
  orderIdAtom,
  orderM3ProductsCheckAtom,
} from 'atoms'
import { getOrderProductAppTypes } from '@/utils/global/isCrossAppTheme'
import { getOrderProducts } from '@/api/orderProducts'
import { getAllProducts } from '@/api/products'

type M3ProductsCheck = { orderId: string | null; hasM3Products: boolean }

// Pure decision core of useIsM3Order, split out for unit testing.
// `isM3Order` — the order should render the M3-style case sections.
// `needsFetch` — the Recoil atoms can't answer (package-flow atoms empty and
// no cached verdict for this order), so the hook must look the products up.
export const resolveIsM3Order = (args: {
  applicationType: string | null | undefined
  atomAppTypes: string[]
  orderId: string | null | undefined
  cache: M3ProductsCheck
}): { isM3Order: boolean; needsFetch: boolean } => {
  const { applicationType, atomAppTypes, orderId, cache } = args
  const resolvedFromAtoms = applicationType === M3 || atomAppTypes.includes(M3)
  const cachedForThisOrder = !!orderId && cache.orderId === orderId
  return {
    isM3Order: resolvedFromAtoms || (cachedForThisOrder && cache.hasM3Products),
    needsFetch:
      !resolvedFromAtoms &&
      atomAppTypes.length === 0 &&
      !!orderId &&
      !cachedForThisOrder,
  }
}

// In-flight lookups keyed by orderId. Many hook instances mount at once
// (every TinyMCE editor uses this) before the Recoil cache fills — sharing
// the promise keeps that to ONE order-products + ONE catalog request per
// order instead of one pair per instance.
const pendingM3Checks = new Map<string, Promise<boolean>>()

const fetchHasM3Products = (orderId: string): Promise<boolean> => {
  const pending = pendingM3Checks.get(orderId)
  if (pending) return pending
  const request = (async () => {
    const [{ data: orderProducts }, { data: catalog }] = await Promise.all([
      getOrderProducts(orderId, undefined, undefined),
      getAllProducts(),
    ])
    return getOrderProductAppTypes(orderProducts, catalog).includes(M3)
  })().finally(() => pendingM3Checks.delete(orderId))
  pendingM3Checks.set(orderId, request)
  return request
}

// Whether the current order is an M3 order — i.e. whether the M3-style case
// sections (flat additional family, date of marriage, genealogy) apply.
//
// A URL-derived applicationType is NOT enough: admins/designers open M3
// master orders directly from MDL, so the atom stays 'MDL' there. Product
// source is the truth (#2482) — an order is M3 when any of its order
// products comes from the m3_products table. The package flow already has
// both inputs in Recoil (`selectedProductsAtom` + the catalog union in
// `productsAtom`); the editor and dashboard never populate them, so this
// hook falls back to fetching the order's products + catalog once per order
// and caching the verdict in `orderM3ProductsCheckAtom`.
export const useIsM3Order = (): boolean => {
  const applicationType = useRecoilValue(applicationTypeAtom)
  const selectedProducts = useRecoilValue(selectedProductsAtom)
  const products = useRecoilValue(productsAtom)
  const orderId = useRecoilValue(orderIdAtom)
  const [m3Check, setM3Check] = useRecoilState(orderM3ProductsCheckAtom)

  const atomAppTypes = useMemo(
    () => getOrderProductAppTypes(selectedProducts, products),
    [selectedProducts, products],
  )

  const { isM3Order, needsFetch } = resolveIsM3Order({
    applicationType,
    atomAppTypes,
    orderId,
    cache: m3Check,
  })

  useEffect(() => {
    // needsFetch already implies a truthy orderId; the explicit check also
    // narrows the type for the shared lookup below.
    if (!needsFetch || !orderId) return
    let cancelled = false
    fetchHasM3Products(orderId)
      .then((hasM3Products) => {
        if (!cancelled) setM3Check({ orderId, hasM3Products })
      })
      .catch(() => {
        // Unresolvable (network, permissions): stay on the MDL sections
        // rather than blocking the panel. Left uncached so a re-mount
        // retries.
      })
    return () => {
      cancelled = true
    }
  }, [needsFetch, orderId, setM3Check])

  return isM3Order
}

export default useIsM3Order
